import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ClipboardCheck, ClipboardX, Send, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * LeaveApply — SE mobile screen.
 * Shows current balances + apply form + own request history.
 */
export default function LeaveApply() {
  const [balances, setBalances] = useState([]);
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ leave_type_code: "", from_date: "", to_date: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: b }, { data: t }, { data: r }] = await Promise.all([
      api.get("/leave/balance"),
      api.get("/lookups", { params: { category: "leave_type" } }),
      api.get("/leave/requests", { params: { page_size: 20 } }),
    ]);
    setBalances(b.items || []);
    setTypes(t.items || []);
    setRequests(r.items || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.leave_type_code || !form.from_date || !form.to_date || !form.reason || form.reason.length < 3) {
      return toast.error("Fill leave type, dates and reason");
    }
    setBusy(true);
    try {
      const body = { ...form, idempotency_key: (crypto.randomUUID?.() || `lv-${Date.now()}`) };
      const r = await offlineMutate({ method: "POST", path: "/leave/apply", body, entity_type: "leave_requests" });
      if (r.immediate) {
        toast.success("Leave applied");
        setForm({ leave_type_code: "", from_date: "", to_date: "", reason: "" });
        load();
      } else {
        toast.info("Queued offline — will sync");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const cancel = async (r) => {
    if (!window.confirm("Cancel this leave?")) return;
    try {
      await api.post(`/leave/${r.id}/cancel`, { notes: "Cancelled by submitter" });
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="leave-apply-screen">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Time off</div>
        <h1 className="text-2xl font-extrabold text-neutral-900">Leave</h1>
      </div>

      {/* Balances */}
      <div className="grid grid-cols-2 gap-2" data-testid="leave-balances">
        {balances.filter((b) => b.entitled > 0).map((b) => (
          <Card key={b.leave_type_code} className="border-neutral-200 rounded-2xl">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400">{b.label}</div>
              <div className="text-2xl font-bold text-neutral-900" data-testid={`leave-balance-${b.leave_type_code}`}>{b.available}</div>
              <div className="text-[10px] text-neutral-500">
                {b.taken} taken · {b.pending} pending / {b.entitled}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Apply form */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-neutral-900">Apply for leave</div>
          <div>
            <Label className="text-[11px]">Type</Label>
            <Select value={form.leave_type_code}
                    onValueChange={(v) => setForm((s) => ({ ...s, leave_type_code: v }))}>
              <SelectTrigger data-testid="leave-apply-type"><SelectValue placeholder="Choose leave type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">From</Label>
              <Input type="date" min={today} value={form.from_date}
                     onChange={(e) => setForm((s) => ({ ...s, from_date: e.target.value }))}
                     data-testid="leave-apply-from" />
            </div>
            <div>
              <Label className="text-[11px]">To</Label>
              <Input type="date" min={form.from_date || today} value={form.to_date}
                     onChange={(e) => setForm((s) => ({ ...s, to_date: e.target.value }))}
                     data-testid="leave-apply-to" />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Reason</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))}
                      placeholder="Family function / medical / …"
                      data-testid="leave-apply-reason" />
          </div>
          <Button onClick={submit} disabled={busy}
                  className="w-full h-12 tulsi-gradient text-white font-bold rounded-xl"
                  data-testid="leave-apply-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Submit
          </Button>
        </CardContent>
      </Card>

      {/* Own requests */}
      <div>
        <div className="text-sm font-semibold text-neutral-900 mb-2 flex items-center gap-2">
          <CalendarDays className="h-4 w-4" /> My requests
        </div>
        <div className="space-y-2" data-testid="leave-my-requests">
          {requests.length === 0 && <div className="text-sm text-neutral-400 py-4 text-center">No requests yet</div>}
          {requests.map((r) => (
            <Card key={r.id} className="border-neutral-200 rounded-xl">
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">
                    {r.leave_type_code.toUpperCase()} · {r.days_count}d
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    {r.from_date} → {r.to_date}
                  </div>
                  <div className="text-[11px] text-neutral-500 truncate">{r.reason}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={r.status} />
                  {r.status === "pending" && (
                    <Button size="sm" variant="ghost" onClick={() => cancel(r)}
                            className="text-red-600 h-7 px-2 text-[11px]"
                            data-testid={`leave-cancel-${r.id.slice(0, 6)}`}>
                      <Ban className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-700",
    cancelled: "bg-neutral-100 text-neutral-600",
  }[status] || "bg-neutral-100 text-neutral-600";
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}
